import { Alert, Badge, Box, Button, FormButtonRow, FormGroup, FormParagraph, Heading } from '@ksyos/design-system';
import IBAN from 'iban';
import { useEffect, useState } from 'react';
import { BankAccountEntity } from '@common/enums/bankAccountEntity';
import type { IBankAccountDetails } from '../../../../common/interfaces/bankAccountDetails';
import { BankAccountDetailsStatus } from '../../../../common/enums/bankAccounkDetailsStatus';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { RequestBankAccountDetailsChangeModal } from '../profile/RequestChangeBankAccountDetailsModal';

interface IBankAccountDetailsProps {
    bankAccountEntity: BankAccountEntity;
    canProcessBankAccountChanges?: boolean;
    canRequestChange: boolean;
    entityDisplayName?: string;
    entityId: string;
    extraBottomElements?: JSX.Element;
}

export function BankAccountDetails(props: IBankAccountDetailsProps) {
    const [currentBankAccountDetails, setCurrentBankAccountDetails] = useState<IBankAccountDetails>();
    const [newBankAccountDetails, setNewBankAccountDetails] = useState<IBankAccountDetails>();
    const [isRequestChangeModalOpen, setIsRequestChangeModalOpen] = useState(false);
    const [showProcessedSuccessfully, setShowProcessedSuccessfully] = useState(false);

    useEffect(() => {
        loadBankAccountDetails();
    }, [props.entityId]);

    return (
        <>
            {newBankAccountDetails && (
                <Heading
                    mb={6}
                    mt={8}
                    size="medium"
                >
                    Huidige rekeninggegevens
                </Heading>
            )}

            {renderBankAccountDetails(currentBankAccountDetails)}
            {newBankAccountDetails && (
                <>
                    <Box
                        display="flex"
                        mb={6}
                        mt={8}
                    >
                        <Heading size="medium">Nieuwe rekeninggegevens</Heading>
                        <Badge
                            color={'magenta'}
                            style={{ marginLeft: '10px', marginTop: 'auto' }}
                            text={'Nog te verwerken'}
                        />
                    </Box>

                    {renderBankAccountDetails(newBankAccountDetails)}

                    {props.canProcessBankAccountChanges &&
                        newBankAccountDetails.status === BankAccountDetailsStatus.requested && (
                            <>
                                <Alert appearance="info">
                                    {props.bankAccountEntity === BankAccountEntity.employment
                                        ? `Er is een verzoek om rekeninggegevens te wijzigen. Verwerk de wijziging in Exact. Na klikken op de knop ‘Verwerkt in Exact’ tonen we aan ${props.entityDisplayName} dat de nieuwe rekeninggegevens vanaf dan geldig zijn.`
                                        : 'Er is een verzoek om rekeninggegevens te wijzigen. Verwerk de wijziging in Exact. Na klikken op de knop ‘Verwerkt in Exact’ tonen we aan de organisatiebeheerder dat de nieuwe rekeninggegevens vanaf dan geldig zijn.'}
                                </Alert>
                                <FormButtonRow>
                                    <Button onClick={async () => await processRequestedChange()}>
                                        Verwerkt in Exact
                                    </Button>
                                </FormButtonRow>
                            </>
                        )}
                </>
            )}

            {showProcessedSuccessfully && (
                <Alert appearance="success">
                    {props.bankAccountEntity === BankAccountEntity.employment
                        ? `Vanaf nu ziet ${props.entityDisplayName} de nieuwe rekeninggegevens als de huidige rekeninggegevens in Uitbetalingen en Persoonlijke instellingen.`
                        : 'Vanaf nu ziet de organisatiebeheerder de nieuwe rekeninggegevens als de huidige rekeninggegevens in Uitbetalingen van de organisatie.'}
                </Alert>
            )}

            {props.extraBottomElements}

            {props.canRequestChange && (
                <Button
                    onClick={() => setIsRequestChangeModalOpen(true)}
                    variant="link"
                >
                    {translateKey('profile.changeAccountDetails')}
                </Button>
            )}

            {isRequestChangeModalOpen && (
                <RequestBankAccountDetailsChangeModal
                    bankAccountEntity={props.bankAccountEntity}
                    currentBankAccountNumber={currentBankAccountDetails?.bankAccountNumber}
                    currentSubscriber={currentBankAccountDetails?.subscriber}
                    entityId={props.entityId}
                    isOpen={isRequestChangeModalOpen}
                    onClose={async () => {
                        await loadBankAccountDetails();
                        setIsRequestChangeModalOpen(false);
                    }}
                />
            )}
        </>
    );

    async function loadBankAccountDetails() {
        const url = `/api/bankAccountDetails?entityId=${props.entityId}&bankAccountEntity=${props.bankAccountEntity}`;

        const result = await fetchJson(url, 'GET');

        const bankAccountDetailsResult: IBankAccountDetails[] = result.json.bankAccountDetails;

        setCurrentBankAccountDetails(
            bankAccountDetailsResult.find(
                (bankAccountDetails: IBankAccountDetails) =>
                    bankAccountDetails.status === BankAccountDetailsStatus.current
            )
        );
        setNewBankAccountDetails(
            bankAccountDetailsResult.find(
                (bankAccountDetails: IBankAccountDetails) =>
                    bankAccountDetails.status === BankAccountDetailsStatus.requested
            )
        );
    }

    async function processRequestedChange() {
        await fetchJson(
            `/api/bankAccountDetails/processChange?entityId=${props.entityId}&bankAccountEntity=${props.bankAccountEntity}`,
            'GET'
        );
        await loadBankAccountDetails();

        setShowProcessedSuccessfully(true);
    }

    function renderBankAccountDetails(bankAccountDetails?: IBankAccountDetails) {
        return (
            <>
                <FormGroup label={translateKey('finance.toIban')}>
                    <FormParagraph>
                        {bankAccountDetails?.bankAccountNumber
                            ? IBAN.printFormat(bankAccountDetails.bankAccountNumber)
                            : translateKey('common.unknown')}
                    </FormParagraph>
                </FormGroup>
                <FormGroup label={translateKey('finance.inNameOf')}>
                    <FormParagraph>{bankAccountDetails?.subscriber ?? translateKey('common.unknown')}</FormParagraph>
                </FormGroup>
            </>
        );
    }
}
